import type { WireCover, WireOnboardingStep } from '@/api/hooks';
import { agoWords, dayOf, daysUntil, firstOf, nowOf, others, personParty, plural, type Viewer } from '@/directory/explainKit';
import type { ItemContext, Party } from '@/ui/context';
import type { WireErasure, WireInvitation, WirePerson, WirePersonDetail } from '@/api/wire';
import type { Who } from '@/ui/who';
import { stateWords } from '@/ui/words';

/**
 * What the people pages say about themselves (ADR-0155): a roster row, a
 * person's page, a cover, an invitation, a data subject's request to be
 * forgotten. Every one of these ends in a person's act under a one-time
 * code that only a superadmin mints, and the tips say so in words rather
 * than naming the operation or the target it is bound to.
 */

const list = (names: string[]): string => (names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);

// --- the roster -----------------------------------------------------------------------

export function explainPerson(p: WirePerson | WirePersonDetail, who: Who, v: Viewer, extra: { onPage?: boolean } = {}): ItemContext {
  const now = nowOf(v);
  const mine = p.id === v.humanId;
  const first = firstOf(p.displayName);
  const departments = p.departments.map(who.label);
  const leads = p.leads.map(who.label);
  const twin = p.twin ? who.actor(p.twin) : null;
  const detail = 'holding' in p ? (p as WirePersonDetail) : null;
  const shredDays = p.state === 'left' && !p.shreddedAt ? daysUntil(p.shredAfter, now) : null;

  const next = ((): ItemContext['next'] => {
    if (p.state === 'left') {
      return p.shreddedAt
        ? { tip: `Nothing to do. ${first}’s personal surfaces were shredded on ${dayOf(p.shreddedAt)}; their work stays as the company’s record under their name.`, instead: 'The page keeps who inherited their work and where their twin’s memory went.' }
        : {
            tip: `Nothing to do unless something of theirs was missed. ${first}’s personal surfaces shred ${shredDays !== null && shredDays >= 0 ? `in ${plural(shredDays, 'day')}` : 'soon'}; what they held open already went to the person named on their departure.`,
            instead: 'Open their page to see who inherited their work and where their twin’s memory went.',
          };
    }
    if (mine) {
      return { tip: 'This is you. Nothing is needed here; if you will be away, arrange who covers you from this page.', instead: 'Your own settings are the other place to look: your twin, your tokens and your preferences.' };
    }
    if (p.departments.length === 0) {
      return {
        tip: `${first} belongs to no department, so they see little. Add a department to their line in the roster: a person commits it, and the next sync gives them access.`,
        instead: 'Or leave it if they are meant to be outside every department for now.',
      };
    }
    if (v.isSuperadmin) {
      return {
        tip: `Nothing is needed from you. When ${first} leaves, open their page and record the departure: access ends in one step, and what they hold open goes to a successor you name.`,
        instead: 'To let the platform act in their own Google account, a superadmin does that from this page, one surface at a time.',
      };
    }
    return {
      tip: `Nothing is needed from you. If ${first} will be away, ${v.leads.some((d) => p.departments.includes(d)) ? 'you can arrange who covers them from their page, as lead of their department' : 'they or their lead arrange cover from their page'}.`,
      instead: 'To ask them something, use the Team tab of the side chat: a conversation there is theirs and yours alone.',
    };
  })();

  const holds = detail?.holding;
  return {
    subject: { kind: 'person', title: p.displayName },
    summary: `${mine ? 'You are' : `${p.displayName} is`} ${p.state === 'left' ? 'a person who has left' : 'here'}${departments.length ? `, in ${list(departments)}` : ', in no department'}${leads.length ? ` and ${mine ? 'lead' : 'leads'} ${list(leads)}` : ''}.${twin ? ` ${mine ? 'Your' : 'Their'} twin is ${twin.name}: no address, it prepares and never sends.` : ''}${p.isSuperadmin ? ' A superadmin.' : ''}`,
    need: p.state === 'left' ? 'Nothing: the departure is done.' : 'Nothing, until their work or their department changes.',
    why: extra.onPage ? 'Everyone can read who works here; what a person holds open is a superadmin’s to see.' : 'The roster lists everyone who works here, and everyone who did. It is a superadmin’s page.',
    // A person joins by a roster line a person commits (ADR-0108); who committed it is not on the wire.
    source: { label: 'Their line in the company roster, which a person committed' },
    broughtBy: null,
    involved: twin ? [twin] : undefined,
    knows: others(p.departments.map((d) => who.lead(d))),
    facts: [
      ['Departments', departments.length ? list(departments) : null],
      ['Leads', leads.length ? list(leads) : null],
      ['Left', p.state === 'left' ? dayOf(p.leftAt) : null],
      ['Personal surfaces shred', p.state === 'left' && !p.shreddedAt ? dayOf(p.shredAfter) : null],
      ['Holds open now', holds ? plural(holds.openTasks, 'open task') + `, ${plural(holds.claimedThreads, 'claimed conversation')}, ${plural(holds.contacts, 'contact')}` : null],
    ],
    next,
    ...(extra.onPage ? {} : { open: { to: `/people/${p.id}` } }),
  };
}

// --- cover ----------------------------------------------------------------------------

export function explainCover(c: WireCover, who: Who, v: Viewer): ItemContext {
  const now = nowOf(v);
  const covered = personParty(v, who, c.covered.id, c.covered.displayName, 'is away') as Party;
  const coverer = personParty(v, who, c.coverer.id, c.coverer.displayName, 'covers') as Party;
  const iCover = c.coverer.id === v.humanId;
  const iAmCovered = c.covered.id === v.humanId;
  const setBy = who.actor(c.setBy);
  const dates = `${dayOf(c.startsOn)} to ${dayOf(c.endsOn)}`;
  const toStart = daysUntil(c.startsOn, now) ?? 0;
  const toEnd = daysUntil(c.endsOn, now) ?? 0;

  const next = ((): ItemContext['next'] => {
    switch (c.state) {
      case 'upcoming':
        return {
          tip: iCover
            ? `You start covering ${firstOf(c.covered.displayName)} ${toStart > 0 ? `in ${plural(toStart, 'day')}` : 'today'}. Nothing is needed now; tasks that would reach them will arrive on your list, marked with whose they were.`
            : `Nothing is needed. From ${dayOf(c.startsOn)}, tasks that would reach ${iAmCovered ? 'you' : firstOf(c.covered.displayName)} go to ${firstOf(c.coverer.displayName)}.`,
          instead: 'If plans changed, end it: the tasks then keep reaching the person they were meant for.',
        };
      case 'active':
        return {
          tip: iCover
            ? `You are covering ${firstOf(c.covered.displayName)} until ${dayOf(c.endsOn)}${toEnd >= 0 ? ` (${plural(toEnd, 'day')} left)` : ''}. Their tasks are on your list; their mail, chats and plan stay theirs.`
            : iAmCovered
              ? `${firstOf(c.coverer.displayName)} is covering you until ${dayOf(c.endsOn)}. Nothing is needed; if you are back early, end it and your tasks come back to you.`
              : `Running until ${dayOf(c.endsOn)}. Nothing is needed; end it early if ${firstOf(c.covered.displayName)} is back.`,
          instead: 'Ending it moves nothing already handed over; only tasks that arrive later come back to the person they were for.',
        };
      case 'over':
        return { tip: 'Nothing to do. The cover ran its dates and has ended by itself.', instead: 'Arrange another from the person’s page if they are away again.' };
      default:
        return { tip: 'Nothing to do. This cover was ended early.', instead: 'Arrange a new one from the person’s page if it is needed again.' };
    }
  })();

  return {
    subject: { kind: 'cover', title: `${firstOf(c.coverer.displayName)} covers ${firstOf(c.covered.displayName)}` },
    summary: `${coverer.name === 'You' ? 'You cover' : `${c.coverer.displayName} covers`} ${covered.name === 'You' ? 'you' : c.covered.displayName}, ${dates}. While it runs, a task that would reach ${covered.name === 'You' ? 'you' : firstOf(c.covered.displayName)} goes to the person covering, marked with whose it was. Nothing else moves.`,
    need: c.state === 'upcoming' || c.state === 'active' ? (iCover ? 'You to pick up the tasks that arrive for them.' : 'Nothing from you.') : 'Nothing.',
    why: iCover ? 'You were named to cover, so their tasks reach you.' : iAmCovered ? 'You will be away, so your tasks go to the person you named.' : 'It sits on this person’s page, where a lead or a superadmin may arrange it.',
    source: setBy ? { label: `Arranged by ${setBy.name === 'You' ? 'you' : setBy.name}` } : null,
    broughtBy: setBy,
    owner: coverer,
    involved: [covered],
    knows: others([covered, who.lead(v.department)]),
    facts: [
      ['First day', dayOf(c.startsOn)],
      ['Last day', dayOf(c.endsOn)],
      ['State', stateWords(c.state)],
      ['Ended', c.endedAt ? `${dayOf(c.endedAt)}${c.endedBy ? ` by ${who.actor(c.endedBy)?.name ?? 'someone'}` : ''}` : null],
    ],
    next,
  };
}

// --- the first days ---------------------------------------------------------------------

export function explainFirstDay(s: WireOnboardingStep, person: { displayName: string }, who: Who, v: Viewer): ItemContext {
  const first = firstOf(person.displayName);
  return {
    subject: { kind: 'step', title: s.label },
    summary: `${s.label}. A step of ${first}’s first days, read from what the platform holds; nobody ticks it by hand.${s.optional ? ' It is optional.' : ''}`,
    need: s.done ? 'Nothing: the platform can see it is done.' : (s.next ?? 'Whoever the step names.'),
    why: 'Each step is checked against what exists, so it clears itself the moment it is true.',
    source: null,
    broughtBy: null,
    knows: others(who.knowers(v.department)),
    next: s.done
      ? { tip: 'Nothing to do. This step is done.', instead: 'It stays listed so you can see the whole of what a first week asks.' }
      : { tip: s.next ? `Still open. ${s.next}.`.replace(/\.\.$/, '.') : 'Still open. It clears by itself when the platform can see it is true.', instead: s.optional ? 'It is optional: skipping it costs nothing.' : 'Nothing else clears it; ticking it by hand is not offered.' },
  };
}

// --- invitations --------------------------------------------------------------------------

export function explainInvitation(i: WireInvitation, who: Who, v: Viewer): ItemContext {
  const now = nowOf(v);
  const by = who.actor(i.invitedBy);
  return {
    subject: { kind: 'invitation', title: i.displayName },
    summary: `${i.displayName} was invited by ${by?.name === 'You' ? 'you' : (by?.name ?? 'a superadmin')}${dayOf(i.invitedAt) ? ` on ${dayOf(i.invitedAt)}` : ''}. ${
      i.state === 'joined' ? 'They have joined.' : 'An invitation gives them nothing: they join when a person commits their lines to the roster and the next sync runs.'
    }`,
    need: i.state === 'joined' ? 'Nothing.' : 'A person to commit the roster lines this made; a Workspace admin creates their account.',
    why: 'Joining is a roster line a person commits, so this only checked the lines and kept them for you.',
    source: { label: 'An invitation a superadmin made in the People page', when: dayOf(i.invitedAt) ?? undefined },
    broughtBy: by ? { ...by, when: dayOf(i.invitedAt) ?? undefined } : null,
    owner: i.state === 'open' ? { name: 'A person who commits to the roster', kind: 'person' } : undefined,
    knows: others([by]),
    facts: [['Invited', agoWords(i.invitedAt, now)]],
    next:
      i.state === 'joined'
        ? { tip: 'Nothing to do. They have joined, and their person page is on the list.', instead: 'Their Workspace account still has to be opened once by them before anything can act as it.' }
        : {
            tip: 'Copy the lines below into the company roster in a commit of their own. Nothing exists for them until it merges and the sync runs.',
            instead: 'Or withdraw the invitation if they are no longer joining; nothing was created, so nothing needs undoing.',
          },
  };
}

// --- a data subject's request to be forgotten ------------------------------------------------

export function explainErasure(r: WireErasure, who: Who, v: Viewer): ItemContext {
  const now = nowOf(v);
  const by = who.actor(r.requestedBy);
  const left = daysUntil(r.dueOn, now) ?? 0;
  const prospect = r.contactId === null;
  const decider = r.decidedBy ? who.actor(r.decidedBy) : null;
  const due = left < 0 ? `overdue by ${plural(-left, 'day')}` : left === 0 ? 'due today' : `due in ${plural(left, 'day')}`;

  const next = ((): ItemContext['next'] => {
    if (r.state === 'executed') {
      const shared = r.outcome?.threadsShared ?? 0;
      return shared > 0
        ? { tip: `${plural(shared, 'conversation')} they share with someone else still hold what they wrote. Redacting those is a person’s work: nothing erased them, because that would erase the others too.`, instead: 'Everything else is done; their address stays on the suppression list so nothing writes to them again.' }
        : { tip: 'Nothing to do. It was carried out, and what was erased is counted below.', instead: 'Their address stays on the suppression list, so nothing writes to them again.' };
    }
    if (r.state === 'refused') {
      return { tip: 'Nothing more on the platform. Make sure the subject has been given the reason it was declined: they are owed it.', instead: r.refusalReason ? `The reason on record: ${r.refusalReason}` : undefined };
    }
    if (v.isSuperadmin) {
      return {
        tip: `${left < 0 ? 'It is overdue. ' : ''}Carry it out from its page: you mint a one-time code bound to this request and nothing else, and spend it there. It is ${due}.`,
        instead: 'Or decline it, only for a legal hold or a contract still running, and with a reason the subject can act on.',
      };
    }
    return {
      tip: `Nothing for you to do. A superadmin carries it out, with a one-time code only they can mint; it is ${due}. Their address is already suppressed, so nothing will write to them.`,
      instead: 'If they get in touch again, tell a superadmin the day it arrived: the month they are owed runs from then.',
    };
  })();

  return {
    subject: { kind: 'erasure request', title: r.contactName },
    summary: `${r.contactName}${prospect ? ' (a business our plans found)' : ''} asked us to forget them. It arrived ${dayOf(r.receivedOn)} and is ${
      r.state === 'requested' ? due : r.state === 'executed' ? `erased${r.decidedAt ? ` on ${dayOf(r.decidedAt)}` : ''}` : 'declined'
    }.`,
    need:
      r.state === 'requested'
        ? v.isSuperadmin
          ? 'You to carry it out or decline it, before the month runs out.'
          : 'A superadmin to carry it out or decline it, before the month runs out.'
        : 'Nothing.',
    why: 'A person who is owed forgetting is owed an answer within a month of the day they asked. Their address was suppressed when it was recorded.',
    source: prospect ? { label: 'Recorded for a business our plans found' } : { label: 'Recorded from the contact’s page', ...(r.state === 'requested' ? { to: `/contacts/${r.contactId}` } : {}), when: dayOf(r.requestedAt) ?? undefined },
    broughtBy: by ? { ...by, when: dayOf(r.requestedAt) ?? undefined } : null,
    owner: r.state === 'requested' ? { name: 'A superadmin', kind: 'person', role: 'decides it' } : decider ? { ...decider, role: r.state === 'executed' ? 'carried it out' : 'declined it' } : null,
    knows: others([by, decider, who.lead(v.department)]),
    facts: [
      ['Received', dayOf(r.receivedOn)],
      ['Due', dayOf(r.dueOn)],
      ['Why declined', r.refusalReason],
      ['Conversations erased', r.outcome ? String(r.outcome.threadsErased) : null],
      ['Left for a person to redact', r.outcome && r.outcome.threadsShared > 0 ? String(r.outcome.threadsShared) : null],
    ],
    next,
    open: { to: `/erasure/${r.id}` },
  };
}
